import { createElement, useMemo, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@fronterahealth/frontera-ui-components/ui/dialog";
import { InputGroup, InputGroupTextarea } from "@fronterahealth/frontera-ui-components/ui/input-group";

import { isSameValue } from "@components/QaReview/PeerReview/peerReview.readableValue";
import { PeerReviewField } from "@components/QaReview/PeerReview/peerReview.suggestedEdit";
import { getReportFieldInput } from "@components/ReportField/registry";

interface SuggestEditDialogProps {
  field: PeerReviewField;
  onCancel: () => void;
  onSubmit: (body: string, proposedValue: string, baseValue: string) => void;
  isSubmitting?: boolean;
}

export const SuggestEditDialog: React.FC<SuggestEditDialogProps> = ({
  field,
  onCancel,
  onSubmit,
  isSubmitting = false,
}) => {
  const [baseValue] = useState(field.content);
  const [proposedValue, setProposedValue] = useState(baseValue);
  const [commentBody, setCommentBody] = useState("");

  const realFieldInput = useMemo(
    () => getReportFieldInput(field.realField.fieldType, field.realField.fieldIdentifier),
    [field.realField.fieldType, field.realField.fieldIdentifier],
  );

  const isUnchanged = isSameValue(field.realField.fieldType, proposedValue, baseValue);

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Suggest an edit — &quot;{field.title}&quot;</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Small className="mb-1 block font-medium text-text-tertiary">Your suggested rewrite</Small>
            {realFieldInput ? (
              createElement(realFieldInput, {
                field: field.realField,
                value: proposedValue,
                onChange: (content: string) => {
                  setProposedValue(content);
                  return Promise.resolve();
                },
                variant: "default",
              })
            ) : (
              <>
                <div className="mb-2 rounded-md border border-limestone-200 bg-limestone-50 px-2 py-1 text-xs whitespace-pre-wrap">
                  {baseValue || "(empty)"}
                </div>
                <InputGroup>
                  <InputGroupTextarea
                    value={proposedValue}
                    onChange={(e) => setProposedValue(e.target.value)}
                    rows={4}
                    autoFocus
                  />
                </InputGroup>
              </>
            )}
          </div>
          <div>
            <Small className="mb-1 block font-medium text-text-tertiary">Comment (optional)</Small>
            <InputGroup>
              <InputGroupTextarea
                placeholder="Explain your suggestion…"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                rows={2}
              />
            </InputGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(commentBody, proposedValue, baseValue)}
            disabled={isSubmitting || !proposedValue.trim() || isUnchanged}
          >
            Send suggestion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
